import { BuildList, BuildListData } from '@/components/BuildList'
import { Callout } from '@/components/Callout'
import { CardImage } from '@/components/CardImage'
import { Hero } from '@/components/Hero'
import { Partners } from '@/components/Partners'
import { PartsList } from '@/components/PartsList'
import { VideoEmbed } from '@/components/VideoEmbed'
import '@/globals/globals'
import { statamicGraphqlEndpoint } from '@/globals/globals'
import { getPartImage } from '@/lib/getPartImage'
import { createVideoObject } from '@/lib/youtube/createVideoObject'
import { Part } from '@/types/part'
import { PartGroup } from '@/types/partGroup'
import { Video } from '@/types/video'
import {
  Box,
  Button,
  Container,
  HStack,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'
import { gql, request } from 'graphql-request'
import { uniqueId } from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

type Season = {
  season_title: string
  episodes: Episode[]
}

type Episode = {
  title: string
  url?: string
  video: Video
}

type PostProps = {
  post: any
  builds: any
  seasons: Season[]
  partsList: PartGroup[]
}

export async function getStaticProps({ params }: any) {
  const query = gql`
    query Entry($slug: String) {
      entry(collection: "builds", slug: $slug) {
        ... on Entry_Builds_Build {
          id
          seo_title
          status
          subheading
          title
          main_content
          date
          slug
          seo_description
          seo_image {
            url
          }
          image {
            url
          }
          parts_list {
            ... on Set_PartsList_PartsGroup {
              partType {
                label
                value
              }
              parts {
                ... on Set_PartsList_Parts_NewSet {
                  name
                  number
                  product_url
                  type
                }
              }
              type
            }
          }
          partners {
            ... on Entry_Partners_Partners {
              id
              website
              logo {
                url
                width
                height
              }
            }
          }
          videos {
            ... on Set_Videos_Season {
              season_title
              episodes {
                title
                url
              }
            }
          }
        }
      }
    }
  `

  const response = await request(statamicGraphqlEndpoint, query, {
    slug: params.slug,
  })

  // Set the embedURL and thumbnail for each video
  const seasonsResponse: Season[] = response.entry?.videos
  let seasons =
    seasonsResponse && seasonsResponse.length > 0
      ? seasonsResponse.map(season => ({
          ...season,
          episodes: season.episodes.map(episode => ({
            title: episode.title,
            url: episode.url,
            video: createVideoObject(episode.url),
          })),
        }))
      : null

  const partsList = response.entry?.parts_list.map(
    (group: {
      partType: { label: string; value: string }
      parts: [
        {
          name: string
          number: string
          product_url: string
        }
      ]
    }): PartGroup => ({
      type: group.partType.value,
      id: uniqueId(),
      icon: getPartImage(group.partType.value),
      name: group.partType.label,
      parts: group.parts?.map(
        (part): Part => ({
          id: uniqueId(),
          name: part.name,
          number: part.number,
          productUrl: part.product_url,
          type: group.partType.value,
        })
      ),
    })
  )

  // 404 if no post found
  if (!response.entry) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      post: response.entry,
      partsList,
      seasons,
      builds: await request(statamicGraphqlEndpoint, BuildListData(), {
        limit: 6,
      }),
    },
    revalidate: 20,
  }
}

export async function getStaticPaths() {
  const query = gql`
    query Entries {
      entries(collection: "builds") {
        data {
          slug
        }
      }
    }
  `

  const response = await request(statamicGraphqlEndpoint, query)

  // check if we want to have a faster build
  const fullBuild = process.env.NEXT_BUILD_TYPE !== 'fast'

  const paths =
    response && response?.entries && response?.entries?.data && fullBuild
      ? response.entries.data.map((entry: any) => ({
          params: { slug: entry.slug },
        }))
      : []

  return {
    paths,
    fallback: true,
  }
}

export default function Post(props: PostProps) {
  const router = useRouter()

  let [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null)
  let [currentSeason, setCurrentSeason] = useState<Season | null>(null)

  // Set a default current season
  useEffect(() => {
    // guards
    if (currentSeason) return
    if (!props.seasons) return

    props.seasons &&
      props.seasons.length > 0 &&
      setCurrentSeason(props.seasons[props.seasons.length - 1] ?? null)
  }, [props.seasons, currentSeason])

  // Set a default current video
  useEffect(() => {
    // guards
    if (currentEpisode) return
    if (!props.seasons) return

    props.seasons.length > 0 &&
      props.seasons[0]?.episodes[0]?.video.embedURL &&
      setCurrentEpisode(
        props.seasons[props.seasons.length - 1]?.episodes[0] ?? null
      )
  }, [props.seasons, currentEpisode])

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  const handleCardClick = (episode: Episode) => {
    setCurrentEpisode(episode)
    document.body.scrollTop = 0
    document.querySelector('#video')?.scrollIntoView()
  }

  return (
    <>
      <Hero
        heading={props.post?.title || 'Loading'}
        subheading={props.post?.subheading}></Hero>

      <Container maxWidth='8xl' py={20} id='video'>
        {currentEpisode ? <VideoEmbed data={currentEpisode.video} /> : null}
      </Container>

      <Container maxWidth='8xl'>
        {/* Season tabs */}
        <HStack>
          {props.seasons.map((season, i) => (
            <Box
              key={season.season_title}
              borderTopWidth='4px'
              borderTopColor={
                currentSeason?.season_title == season.season_title
                  ? 'red.400'
                  : 'white'
              }>
              <Button
                backgroundColor={
                  currentSeason?.season_title == season.season_title
                    ? 'white'
                    : 'gray.200'
                }
                _focus={{ outline: 'none' }}
                _focusVisible={{ backgroundColor: 'gray.200' }}
                _hover={{ backgroundColor: 'gray.100' }}
                onClick={() => {
                  setCurrentSeason(props.seasons[i] ?? null)
                }}>
                {season.season_title}
              </Button>
            </Box>
          ))}
        </HStack>

        {/* Video list */}
        <SimpleGrid
          columns={{ base: 1, md: 3, xl: 4 }}
          spacing={4}
          p={4}
          backgroundColor='white'>
          {currentSeason?.episodes &&
            currentSeason.episodes.map(episode => (
              <Box
                key={episode.video.uuid}
                as='a'
                display='block'
                p={1}
                cursor='pointer'
                _hover={{ bgColor: 'gray.300' }}
                onClick={() => {
                  handleCardClick(episode)
                }}
                bgColor={
                  currentEpisode &&
                  episode.video.embedURL == currentEpisode.video.embedURL
                    ? 'red.400'
                    : 'white'
                }
                color={
                  currentEpisode &&
                  episode.video.embedURL == currentEpisode.video.embedURL
                    ? 'white'
                    : 'gray.900'
                }>
                <Box maxW='100%'>
                  <CardImage
                    alt={episode.title || ''}
                    src={
                      episode.video?.thumbnail ||
                      'http://debossgarage.s3.amazonaws.com/general/image-placeholder-not-found.png'
                    }
                    height={720}
                    width={1280}
                  />
                </Box>
                <Text mt={2} fontSize='xs' fontWeight='bold'>
                  {episode.title || 'Loading'}
                </Text>
              </Box>
            ))}
        </SimpleGrid>
      </Container>
      <Box px={8} className='prose'>
        {props.post?.content_blocks &&
          props.post?.content_blocks?.map((content: any, index: any) => (
            <Text
              key={index}
              dangerouslySetInnerHTML={{ __html: content.text }}></Text>
          ))}
      </Box>
      <Box mb={24}>
        {props.post?.partners.length > 0 && (
          <Partners
            partners={props.post.partners}
            heading='Partners for this build include…'
          />
        )}
      </Box>

      {props.partsList && props.partsList.length > 0 && (
        <Container maxW='4xl' mb={32}>
          <Heading textAlign='center'>Parts used in this build</Heading>
          <Text fontSize='lg' textAlign='center' mb={8}>
            Note: Commissions may be earned from the links below.
          </Text>
          <PartsList partsList={props.partsList} />
        </Container>
      )}

      <Heading textAlign='center'>More Builds</Heading>
      <BuildList builds={props.builds} />
      <Box p={20}>
        <Callout href='/builds' title='See All Builds' />
      </Box>
    </>
  )
}
